import Debug from 'debug';
import { html, PropertyValues } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { sqliteBroker } from '../services/SqliteBroker';
import { PdqSqliteStatement } from './pdq-sqlite-statement';
import { tableBroker } from '../services/TableBroker';
import { Table,Schema,DataRow } from '@finos/perspective';
import { PdfSqlitePreparedRunBulkStatement, PqdSqlClient } from './pdq-sqlite-client';
import { Mutex, waitWithLogging } from '../utils';

export enum PdqSqlitePerspectiveLinkStatus {
  NOT_LOADED = 0,
  SQLITE_READY = 1 << 0,
  PERSPECTIVE_VIEW_READY = 1 << 1,
  SCHEMA_APPLIED = 1 << 2,
  SCHEMA_ERROR = 1 << 3
}

const internalLog = Debug('pdq:sqlite:link');
const log = (status: PdqSqlitePerspectiveLinkStatus, msg: string, ...args: any[]) => internalLog(`[${logStatus(status)}] ${msg}`, status, ...args)
@customElement('pdq-sqlite-perspective-link')
export class PdqSqlitePerspectiveLink extends PdqSqliteStatement  {
  private _refreshMutex = new Mutex('pdq:sqlite:refresh');
  private _refreshPromise: Promise<void>|null = null;
  private _firstUpdateComplete: boolean = false;
  private _tableSubscriptionUnsubscribe = () => {};
  private _client: undefined|PqdSqlClient;
  private _data: undefined|Array<DataRow>;
  private _schema: undefined|Schema;
  private _status: PdqSqlitePerspectiveLinkStatus = PdqSqlitePerspectiveLinkStatus.NOT_LOADED;
  private _insertStatement: PdfSqlitePreparedRunBulkStatement | undefined;

  private _table: undefined|string;
  @property({type: String})
  get table(): string | undefined {
    return this._table;
  }
  set table(value: string | undefined) {
    log(this._status, `Setting table to ${value}`);
    this._table = value;

    this.setupTableSubscription(value!)
      .catch(e => log(this._status, `Error setting up table subscription: ${e}`))
  }
  private _sqliteTable: undefined|string;
  @property({type: String, attribute: "sqlite-table"})
  get sqliteTable(): string | undefined {
    return this._sqliteTable;
  }
  set sqliteTable(value: string | undefined) {
    log(this._status, `Setting sqlite table to ${value}`)
    this._sqliteTable = value;
    this.setupSqliteTable()
  }

  render() {
    return html`
      <slot style="display: none" name="query" @slotchange="${this._onSlotChange}"></slot>
    `;
  }
  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    log(this._status, 'firstUpdated')
    this._firstUpdateComplete = true
    this.refresh()
  }

  async refresh(): Promise<void> {
    const unlock = await this._refreshMutex.lock();
    try {
      if (this._refreshPromise) {
        return await this._refreshPromise;
      }
      this._refreshPromise = this._refresh();
      await this._refreshPromise;
    } finally {
      this._refreshPromise = null;
      unlock();
    }
  }

  async _refresh() {
    if (!this._firstUpdateComplete
      || !(this._status & PdqSqlitePerspectiveLinkStatus.PERSPECTIVE_VIEW_READY)
      || !(this._status & PdqSqlitePerspectiveLinkStatus.SQLITE_READY)
    )
      return

    if (this._status & PdqSqlitePerspectiveLinkStatus.SCHEMA_ERROR) {
      log(this._status, `WARNING: schema error, skipping refresh`)
      return
    }

    if (!this._client) {
      log(this._status, `WARNING: database is not set, query cannot be executed`)
      return
    }
    if (!this._schema) {
      log(this._status, `WARNING:  table are unset, no where to save the query results... skipping`)
      return
    }


    await waitWithLogging((...args: any[]) => log(this._status, ...args), sqliteBroker.getDatabaseReadyPromise(this.database!),
      `Waiting for database[${this.database}] to be ready...`)

    const start = performance.now()
    try {
      if (!(this._status & PdqSqlitePerspectiveLinkStatus.SCHEMA_APPLIED)) {
        log(this._status, 'applying schema to sqlite')
        await this.updateTableSchema()
      }
      await this.insertRows(this._client, this.sqliteTable!, this._data!)
      this.dispatchEvent(new CustomEvent('reload', {
        detail: {
          table: this.table,
          schema: this._schema,
        }
      }));
      if (this.database && this.table)
        sqliteBroker.notifyTableUpdated(this.database, this.table)
      log(this._status, `Refreshed in ${performance.now() - start}ms`)

    }catch (e) {
      log(this._status, `Refresh failed in ${performance.now() - start}ms - error:`, e)

    }
  }

  private async setupTableSubscription (value: string) {
    this._status &= ~PdqSqlitePerspectiveLinkStatus.PERSPECTIVE_VIEW_READY
    this._status &= ~PdqSqlitePerspectiveLinkStatus.SCHEMA_APPLIED


    this._tableSubscriptionUnsubscribe();

    let tableSubscription = {unsubscribe: () => {}};
    tableSubscription = tableBroker.getTable(value)
    .subscribe(async (table: Table) => {
      log(this._status, `Got table ${value}`)

      const view = await table.view();
      [this._schema, this._data] = await Promise.all([table.schema(), view.to_json()]);
      this._status |= PdqSqlitePerspectiveLinkStatus.PERSPECTIVE_VIEW_READY
      view.on_update(i => {
        log(this._status, `view data updated - TODO!!!!`)
        // debugger
      });
      view.on_delete(() => {
        log(this._status, `view deleted - TODO!!!!`)
        // debugger
      });

      await this.refresh()
      this._tableSubscriptionUnsubscribe = async () => {
        await  view.delete()
        tableSubscription.unsubscribe()
      }
    });
  }

  private async setupSqliteTable() {
    this._client = await sqliteBroker.getDatabaseClient(
      this.database!,
      this.alias,
      `pdq:sqlite:link[${this.table}/${this.sqliteTable}]`)
    this._status |= PdqSqlitePerspectiveLinkStatus.SQLITE_READY
    await this.refresh()
  }

  private async insertRows(client: any, table: string, rows: any[]) {
    log(this._status, `Inserting ${rows.length} rows into ${table}`)
    // await client.query("BEGIN TRANSACTION");
    // try {
    //   for (const row of rows) {
    //     const param = {} as any
    //     for (const c in row) {
    //       param[`:${c}`] = row[c]
    //     }
    //     log(this._status, "PARAM!", param);
    //     await client.query(this._insertStatement, param);
    //   }
    //   await client.query("COMMIT");
    // } catch (error) {
    //   await client.query("ROLLBACK");
    //   throw error;
    // }

    await this._insertStatement?.run(
      rows.map(row => {
        const param = {} as any
        for (const c in row) {
          param[`:${c}`] = row[c]
        }
        return param
      })
    );
  }

  private toSqlType(perspectiveType: string): string {
    const typeMap: { [key: string]: string } = {
      "int": "INTEGER",
      "float": "REAL",
      "string": "TEXT",
      "boolean": "INTEGER", // SQLite doesn't have a native boolean type, uses 0/1 as INTEGER.
      "datetime": "TEXT",  // Stored as ISO 8601 string.
      "date": "TEXT",      // Stored as ISO 8601 date string.
      "object": "TEXT",    // Serialized JSON string.
      "decimal": "NUMERIC",
      "list": "TEXT"       // Serialized JSON string.
    };

    return typeMap[perspectiveType] || "TEXT"; // Default to TEXT for unknown types.
  }

  private async updateTableSchema() {
    log(this._status, 'updateTableSchema');
    this._status &= ~PdqSqlitePerspectiveLinkStatus.SCHEMA_ERROR
    this._status |= PdqSqlitePerspectiveLinkStatus.SCHEMA_APPLIED

    if(this._insertStatement)
      await this._insertStatement.dispose()

    const client = this._client!
    await client.query(`DROP TABLE IF EXISTS [${this.sqliteTable}]`)

    const schema = this._schema!;
    const colDefs = []
    const colNames = []
    for (const colName in schema) {
      const type = schema[colName];
      const sqlType = this.toSqlType(type)
      colDefs.push(`[${colName}] ${sqlType}`)
      colNames.push(colName)
    }

    if (colDefs.length === 0) {
      this._status |= PdqSqlitePerspectiveLinkStatus.SCHEMA_ERROR
      throw new Error(`No columns in schema`);
    }
    await client.query(`CREATE TABLE IF NOT EXISTS [${this.sqliteTable}]  (${colDefs.join(', ')})`)
    this._insertStatement = await client.prepareRunBulk(`INSERT INTO [${this.sqliteTable}] 
  (${colNames.map(i => `[${i}]`).join(', ')}) 
VALUES 
  (${colNames.map(i => `:${i}`).join(', ')})`);

  }

  disconnectedCallback() {
    log(this._status,`disconnectedCallback: removing any subscriptions`)

    this._tableSubscriptionUnsubscribe()
    if (this._insertStatement)
      this._insertStatement.dispose()
      .catch(e => log(this._status, `failed to dispose statement: ${e}`))

    super.disconnectedCallback();
  }
}

function logStatus(status: PdqSqlitePerspectiveLinkStatus): string {
  if (status === PdqSqlitePerspectiveLinkStatus.NOT_LOADED) {
    return "NOT_LOADED";
  }

  return Object.keys(PdqSqlitePerspectiveLinkStatus)
  .filter((key) => isNaN(Number(key))) // Filter out numeric keys
  .filter((key) => {
    const value = PdqSqlitePerspectiveLinkStatus[key as keyof typeof PdqSqlitePerspectiveLinkStatus];
    return (status & value) !== 0;
  })
  .join(", ");
}





